import { useEffect, useState } from 'react';
import type { CheckInRow } from '@st/contract';
import { useCheckInHistory, useCurrentCheckIn, useSubmitCheckIn } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { formatDate } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  GroupLabel,
  NumberInput,
  ScaleInput,
  SkeletonCard,
  SkeletonList,
  Textarea,
  useToast,
  type BadgeTone,
} from '../../ui';

const STATUS_TONE: Record<CheckInRow['status'], BadgeTone> = {
  open: 'neutral',
  submitted: 'info',
  reviewed: 'success',
};

const STATUS_KEY: Record<CheckInRow['status'], string> = {
  open: 'checkin.statusOpen',
  submitted: 'checkin.statusSubmitted',
  reviewed: 'checkin.statusReviewed',
};

interface FormState {
  weightKg: number | '';
  energy: number | null;
  sleep: number | null;
  nutritionAdherence: number | '';
  trainingAdherence: number | '';
  comment: string;
}

function initialForm(row: CheckInRow): FormState {
  return {
    weightKg: row.weightKg ?? '',
    energy: row.energy,
    sleep: row.sleep,
    nutritionAdherence: row.nutritionAdherence ?? '',
    trainingAdherence: row.trainingAdherence ?? '',
    comment: row.comment ?? '',
  };
}

/**
 * Tjedni check-in klijenta.
 *
 * Kad trener odgovori, obrazac je ZAKLJUČAN i to se vidi prije klika — server
 * bi na slanje vratio 409, a poruka o grešci nakon ispunjenog obrasca je
 * najgori mogući način da se to sazna.
 */
export function CheckIn() {
  const { t, locale } = useTranslation();
  const { toast } = useToast();
  const current = useCurrentCheckIn();
  const history = useCheckInHistory();
  const submit = useSubmitCheckIn();

  const [form, setForm] = useState<FormState | null>(null);
  const currentId = current.data?.id ?? null;

  useEffect(() => {
    if (current.data) setForm(initialForm(current.data));
  }, [current.data]);

  async function onSubmit(): Promise<void> {
    if (!currentId || !form) return;
    try {
      await submit.mutateAsync({
        id: currentId,
        values: {
          weightKg: form.weightKg === '' ? null : form.weightKg,
          energy: form.energy,
          sleep: form.sleep,
          nutritionAdherence: form.nutritionAdherence === '' ? null : form.nutritionAdherence,
          trainingAdherence: form.trainingAdherence === '' ? null : form.trainingAdherence,
          comment: form.comment.trim() === '' ? null : form.comment.trim(),
        },
      });
      toast(t('checkin.submitted'), 'success');
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  return (
    <>
      <PageHeader title={t('checkin.title')} />

      <div className="flex flex-col gap-6">
        <QueryBoundary query={current} skeleton={<SkeletonCard />}>
          {(row) => {
            const locked = row.status === 'reviewed';
            const submitted = row.status === 'submitted';
            const values = form ?? initialForm(row);

            return (
              <Card>
                <CardHeader
                  eyebrow={t('checkin.week', {
                    date: formatDate(`${row.weekStart}T00:00:00.000Z`, locale),
                  })}
                  title={t('checkin.title')}
                  action={<Badge tone={STATUS_TONE[row.status]}>{t(STATUS_KEY[row.status])}</Badge>}
                />

                {locked && (
                  <p
                    data-testid="checkin-locked"
                    className="mb-4 rounded-md border border-line-subtle bg-bg-alt px-4 py-3 text-[13.5px] text-ink"
                  >
                    {t('checkin.locked')}
                  </p>
                )}
                {submitted && (
                  <p className="mb-4 rounded-md border border-line-subtle bg-bg-alt px-4 py-3 text-[13.5px] text-ink-muted">
                    {t('checkin.awaiting')}
                  </p>
                )}

                <fieldset disabled={locked} className="flex flex-col gap-5">
                  {/*
                    Redoslijed je promijenjen: prije su prve stajale brojke, a
                    poruka zadnja — a upravo nju trener čita prvi. Uz to nigdje
                    nije pisalo da je sve neobavezno, pa je djelomičan check-in
                    izgledao kao nešto što se ne smije poslati.
                  */}
                  <p className="text-[13.5px] leading-relaxed text-ink-muted">{t('checkin.formLead')}</p>

                  <GroupLabel naslov={t('checkin.stepHow')} />
                  <div className="grid gap-5 sm:grid-cols-2">
                    <ScaleInput
                      id="ci-energy"
                      label={t('checkin.energy')}
                      hint={t('checkin.energyHelp')}
                      value={values.energy}
                      disabled={locked}
                      onChange={(v) => setForm({ ...values, energy: v })}
                    />
                    <ScaleInput
                      id="ci-sleep"
                      label={t('checkin.sleep')}
                      hint={t('checkin.sleepHelp')}
                      value={values.sleep}
                      disabled={locked}
                      onChange={(v) => setForm({ ...values, sleep: v })}
                    />
                  </div>


                  <GroupLabel naslov={t('checkin.stepNumbers')} oznaka={t('nutrition.optional')} />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <NumberInput
                      id="ci-weight"
                      label={t('checkin.weight')}
                      suffix="kg"
                      step={0.1}
                      value={values.weightKg}
                      onValueChange={(v) => setForm({ ...values, weightKg: v })}
                    />
                    <div className="grid grid-cols-2 gap-4 sm:contents">
                      <NumberInput
                        id="ci-nutrition"
                        label={t('checkin.nutritionAdherence')}
                        suffix="%"
                        min={0}
                        max={100}
                        value={values.nutritionAdherence}
                        onValueChange={(v) => setForm({ ...values, nutritionAdherence: v })}
                      />
                      <NumberInput
                        id="ci-training"
                        label={t('checkin.trainingAdherence')}
                        suffix="%"
                        min={0}
                        max={100}
                        value={values.trainingAdherence}
                        onValueChange={(v) => setForm({ ...values, trainingAdherence: v })}
                      />
                    </div>
                  </div>


                  <GroupLabel naslov={t('checkin.stepMessage')} oznaka={t('nutrition.optional')} />
                  <p className="text-[12.5px] text-ink-muted">{t('checkin.stepMessageHint')}</p>
                  <Textarea
                    id="ci-comment"
                    label={t('checkin.comment')}
                    placeholder={t('checkin.commentPlaceholder')}
                    value={values.comment}
                    onChange={(e) => setForm({ ...values, comment: e.target.value })}
                  />

                  <div className="flex justify-end">
                    <Button
                      loading={submit.isPending}
                      disabled={locked}
                      onClick={() => void onSubmit()}
                    >
                      {t('checkin.submit')}
                    </Button>
                  </div>
                </fieldset>

                {row.trainerResponse && (
                  <div className="mt-6 rounded-md border border-line-subtle bg-bg-alt p-4">
                    <p className="eyebrow mb-2">{t('checkin.trainerResponse')}</p>
                    <p className="text-[14.5px] leading-relaxed text-ink">{row.trainerResponse}</p>
                  </div>
                )}
              </Card>
            );
          }}
        </QueryBoundary>

        <section>
          <h2 className="eyebrow mb-3">{t('checkin.history')}</h2>
          <QueryBoundary
            query={history}
            skeleton={<SkeletonList rows={2} />}
            isEmpty={(d) => d.items.filter((c) => c.submittedAt !== null).length === 0}
            empty={{ message: t('checkin.noHistory') }}
          >
            {(data) => (
              <ul className="flex flex-col gap-3">
                {data.items
                  .filter((c) => c.submittedAt !== null && c.id !== currentId)
                  .map((row) => (
                    <li key={row.id}>
                      <Card as="article">
                        <CardHeader
                          eyebrow={formatDate(`${row.weekStart}T00:00:00.000Z`, locale)}
                          title={
                            row.weightKg !== null
                              ? `${row.weightKg} kg`
                              : t(STATUS_KEY[row.status])
                          }
                          action={
                            <Badge tone={STATUS_TONE[row.status]}>{t(STATUS_KEY[row.status])}</Badge>
                          }
                        />
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] sm:grid-cols-4">
                          <Stat label={t('checkin.energy')} value={row.energy} suffix="/5" />
                          <Stat label={t('checkin.sleep')} value={row.sleep} suffix="/5" />
                          <Stat
                            label={t('checkin.nutritionAdherence')}
                            value={row.nutritionAdherence}
                            suffix="%"
                          />
                          <Stat
                            label={t('checkin.trainingAdherence')}
                            value={row.trainingAdherence}
                            suffix="%"
                          />
                        </dl>
                        {row.comment && (
                          <p className="mt-3 text-[14px] leading-relaxed text-ink-muted">
                            {row.comment}
                          </p>
                        )}
                        <p className="mt-3 rounded-md bg-bg-alt px-3 py-2 text-[13.5px] text-ink">
                          <span className="eyebrow mr-2">{t('checkin.trainerResponse')}</span>
                          {row.trainerResponse ?? t('checkin.noResponseYet')}
                        </p>
                      </Card>
                    </li>
                  ))}
              </ul>
            )}
          </QueryBoundary>
        </section>
      </div>
    </>
  );
}

function Stat({ label, value, suffix }: { label: string; value: number | null; suffix: string }) {
  return (
    <div>
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-ink tabular-nums">{value === null ? '—' : `${value}${suffix}`}</dd>
    </div>
  );
}
